"use client";
import { IBaseLabelValue } from "@/interfaces";
import {
  Accordion,
  CustomButton,
  FlexBetween,
  FlexCenter,
  FlexCol,
} from "@/components";
import { cn } from "@/lib";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import { routes } from "@/routes";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import React, { useRef, useState } from "react";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import {
  IChequeChain,
  IRawPurchasedChequeTransfersChainInquiryResData,
} from "@/interfaces/inquiries/chequeInquiry";

const CHEQUE_TYPE_MAP = {
  "1": "عادی",
  "2": "بانکی (چک تضمینی)",
  "3": "رمزدار",
  "4": "چک موردی",
};
const CUSTOMER_MAP = {
  name: "نام مشتری ذی نفع",
  idCode: "کد شناسایی دارنده چک",
  idType: "نوع دارنده چک",
};
const CUSTOMER_TYPE_MAP = {
  "1": "شخص حقیقی",
  "2": "شخص حقوقی",
  "3": "اتباع بیگانه حقیقی",
  "4": "اتباع بیگانه حقوقی",
};
const ROLE_TYPE_MAP = {
  1: "صادرکننده چک",
  2: "گیرنده چک (دریافت کننده چک)",
  3: "گیرنده انتقال",
};

const chainMockData = [
  {
    customers: [
      {
        name: "شخص تستی یک",
        idCode: "758478",
        idType: "1",
      },
      {
        name: "شخص تستی 2",
        idCode: "758478",
        idType: "2",
      },
      {
        name: "شخص تستی 3",
        idCode: "758478",
        idType: "3",
      },
      {
        name: "شخص تستی 4",
        idCode: "758478",
        idType: "4",
      },
    ],
    roleType: 1,
  },
  {
    customers: [
      {
        name: "شخص تستی یک",
        idCode: "758478",
        idType: "1",
      },
      {
        name: "شخص تستی 2",
        idCode: "758478",
        idType: "2",
      },
      {
        name: "شخص تستی 3",
        idCode: "758478",
        idType: "3",
      },
      {
        name: "شخص تستی 4",
        idCode: "758478",
        idType: "4",
      },
    ],
    roleType: 2,
  },
  {
    customers: [
      {
        name: "شخص تستی یک",
        idCode: "758478",
        idType: "1",
      },
      {
        name: "شخص تستی 2",
        idCode: "758478",
        idType: "2",
      },
      {
        name: "شخص تستی 3",
        idCode: "758478",
        idType: "3",
      },
      {
        name: "شخص تستی 4",
        idCode: "758478",
        idType: "4",
      },
    ],
    roleType: 3,
  },
];

const ChequeTransferChainDetails = ({
  data,
}: {
  data: IRawPurchasedChequeTransfersChainInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  // Older purchases contain one extra `data` wrapper, while current API
  // responses return the chain directly inside `result`.
  const inquiryResult =
    "result" in data.data.data
      ? data.data.data
      : data.data.data.data;
  const chequeData = inquiryResult?.result?.chain ?? [];
  const chequeInput = data.data.input;
  const [openAccordionId, setOpenAccordionId] = useState<number | null>(null);

  const inputContent: IBaseLabelValue[] = [
    {
      label: t("chequeTransferChain.nationalCode"),
      value: chequeInput.national_code,
    },
    {
      label: t("chequeTransferChain.sayadId"),
      value: chequeInput.sayad_id,
    },
    {
      label: t("chequeTransferChain.chequeType"),
      value:
        CHEQUE_TYPE_MAP[
          chequeInput.cheque_type as keyof typeof CHEQUE_TYPE_MAP
        ],
    },
  ];

  const pdfContentRef = useRef<HTMLDivElement>(null);

  const buildChainPdfData = (chains: IChequeChain[]): string[][] => {
    const rows: string[][] = [];

    chains.forEach((chain, chainIndex) => {
      // نقش
      rows.push([
        "نقش",
        ROLE_TYPE_MAP[chain.roleType as keyof typeof ROLE_TYPE_MAP] ?? "-",
      ]);

      chain.customers.forEach((customer, customerIndex) => {
        // اطلاعات مشتری فعلی
        rows.push([CUSTOMER_MAP.name, customer.name || "-"]);
        rows.push([CUSTOMER_MAP.idCode, customer.idCode || "-"]);
        rows.push([
          CUSTOMER_MAP.idType,
          CUSTOMER_TYPE_MAP[
            customer.idType as keyof typeof CUSTOMER_TYPE_MAP
          ] ?? "-",
        ]);

        // تیتر مشتری بعدی (اگر وجود دارد)
        const nextCustomerIndex = customerIndex + 1;
        if (nextCustomerIndex < chain.customers.length) {
          rows.push([`مشخصات مشتری ${nextCustomerIndex + 1}`, ""]);
        }
      });

      // جداکننده نقش‌ها
      if (chainIndex < chains.length - 1) {
        rows.push(["—", "—"]);
      }
    });

    return rows;
  };
  const pdfData = buildChainPdfData(chequeData);
  return (
    <div className="shadow-main from-emerald-mint relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          استعلام زنجیره انتقالات چک صیادی
        </h2>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        <FlexBetween className="items-center md:mb-3">
          <h2 className="my-5 text-center text-lg font-bold md:my-0">
            {t("accountToIban.resultTitle")}
          </h2>
          <DownloadPdf
            id="cheque-transfer-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            fileName={`استعلام-زنجیره-انتقال-چک-صیادی-${chequeInput.sayad_id}`}
            tableTitle="استعلام زنجیره انتقال چک صیادی"
          />
        </FlexBetween>
        {chequeData.length === 0 ? (
          <p className="my-6 text-center text-sm text-gray-500">
            اطلاعاتی از زنجیره انتقالات این چک یافت نشد.
          </p>
        ) : chequeData.map((chain, i) => (
          <Accordion
            key={i}
            title={
              <>
                <span className="text-sm md:text-base">نقش مشتری :</span>
                <span className="mr-2 text-sm md:text-base">
                  {ROLE_TYPE_MAP[chain.roleType as keyof typeof ROLE_TYPE_MAP]}
                </span>
              </>
            }
            open={chain.roleType === openAccordionId}
            className="mb-2"
            onOpenChange={(isOpen) => {
              setOpenAccordionId(isOpen ? chain.roleType : null);
            }}
          >
            <div
              className={cn(
                "shadow-main mb-10 flex flex-col gap-4 rounded-xl p-4",
              )}
            >
              {chain.customers.map((customer, index) => (
                <>
                  <ul key={index} className="flex flex-col gap-4">
                    <li className="flex-between flex items-start gap-6 text-xs md:gap-8 md:text-sm">
                      <span className="min-w-max">نام مشتری ذی نفع</span>
                      <div className="text-primary-main flex items-center gap-2 text-start">
                        {customer.name}
                      </div>
                    </li>
                    <li
                      key={index}
                      className="flex-between flex items-start gap-6 text-xs md:gap-8 md:text-sm"
                    >
                      <span className="min-w-max">کد شناسایی دارنده چک</span>
                      <div className="text-primary-main flex items-center gap-2 text-start">
                        {customer.idCode}
                      </div>
                    </li>
                    <li
                      key={index}
                      className="flex-between flex items-start gap-6 text-xs md:gap-8 md:text-sm"
                    >
                      <span className="min-w-max">نوع دارنده چک</span>
                      <div className="text-primary-main flex items-center gap-2 text-start">
                        {
                          CUSTOMER_TYPE_MAP[
                            customer.idType as keyof typeof CUSTOMER_TYPE_MAP
                          ]
                        }
                      </div>
                    </li>
                  </ul>
                  {index !== chain.customers.length - 1 && (
                    <hr style={{ color: "#e5e7eb" }} />
                  )}
                </>
              ))}
            </div>
          </Accordion>
        ))}

        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="cheque-transfer-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            fileName={`استعلام-زنجیره-انتقال-چک-صیادی-${chequeInput.sayad_id}`}
            tableTitle="استعلام زنجیره انتقال چک صیادی"
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-cheque-transfer-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-cheque-transfer-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Transfer_Chain_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          resultContent={pdfData}
          code={data.code}
          dateTime={data.created_at}
          tableTitle="اطلاعات چک"
        />
      </div>
    </div>
  );
};

export default ChequeTransferChainDetails;
